import React from 'react';
import { getIconForPole } from './RadarIcons';

const RadarChart = ({ axes, values, color = '#FF6B6B', size = 400, showRingLabels = true, showPoleLabels = true, showBalancedReference = true }) => {
  const center = size / 2;
  const radius = size * 0.25;
  const n = axes.length;

  // Calculate angle for each axis (starting from top, going clockwise)
  const angleForIndex = (i) => {
    return -Math.PI / 2 + (2 * Math.PI * i) / n;
  };

  // Generate points for a polygon at given radius fraction
  const polygonPoints = (radiusFraction) => {
    return Array.from({ length: n }, (_, i) => {
      const angle = angleForIndex(i);
      return {
        x: center + radius * radiusFraction * Math.cos(angle),
        y: center + radius * radiusFraction * Math.sin(angle)
      };
    });
  };

  // Generate concentric web polygons
  // Fewer, clearer rings for readability
  const webLevels = [0.25, 0.5, 0.75, 1.0];
  const webPaths = webLevels.map(level => {
    const points = polygonPoints(level);
    const pathData = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
    ).join(' ') + ' Z';
    return pathData;
  });

  // Generate radial lines
  const radialLines = Array.from({ length: n }, (_, i) => {
    const angle = angleForIndex(i);
    return {
      x1: center,
      y1: center,
      x2: center + radius * Math.cos(angle),
      y2: center + radius * Math.sin(angle)
    };
  });

  // Generate data polygon from normalized values
  const dataPoints = values.map((value, i) => {
    const angle = angleForIndex(i);
    const r = radius * Math.max(0, Math.min(1, value));
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });

  const dataPath = dataPoints.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
  ).join(' ') + ' Z';

  // Generate balanced reference polygon (all values at 0.5 / 50%)
  const balancedPoints = Array.from({ length: n }, (_, i) => {
    const angle = angleForIndex(i);
    const r = radius * 0.5;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });

  const balancedPath = balancedPoints.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
  ).join(' ') + ' Z';

  // Generate axis labels - one label per axis showing both poles
  const axisLabels = axes.map((axis, i) => {
    const angle = angleForIndex(i);
    const labelOffset = 120;
    
    const labelX = center + (radius + labelOffset) * Math.cos(angle);
    const labelY = center + (radius + labelOffset) * Math.sin(angle);
    
    const parts = axis.includes('↔') ? axis.split('↔') : [axis];
    const leftPole = (parts[0] || '').trim();
    const rightPole = (parts[1] || '').trim();
    const fullLabel = `${leftPole} vs. ${rightPole}`;
    
    // Calculate which pole user leans toward
    const v = Math.max(0, Math.min(1, values[i] ?? 0));
    const pct = Math.round(v * 100);
    let leaning = '';
    let leaningDirection = 'balanced';
    
    if (v > 0.55) {
      leaning = rightPole;
      leaningDirection = 'right';
    } else if (v < 0.45) {
      leaning = leftPole;
      leaningDirection = 'left';
    } else {
      leaning = 'Balanced';
      leaningDirection = 'balanced';
    }

    return {
      x: labelX,
      y: labelY,
      leftPole,
      rightPole,
      fullLabel,
      leaning,
      leaningDirection,
      percentage: pct,
      angle
    };
  });

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxWidth: '100%', height: 'auto' }}
    >
      {/* Ring percentage labels */}
      {showRingLabels && webLevels.map((lvl, idx) => {
        const ringLabelSize = size < 700 ? '8px' : '10px';
        return (
          <text
            key={`ring-${idx}`}
            x={center}
            y={center - radius * lvl}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: ringLabelSize, fill: '#999' }}
          >
            {Math.round(lvl * 100)}%
          </text>
        );
      })}
      {/* Concentric web */}
      {webPaths.map((path, i) => (
        <path
          key={`web-${i}`}
          d={path}
          fill="none"
          stroke="#999"
          strokeWidth="1.5"
          opacity="0.3"
        />
      ))}

      {/* Radial lines */}
      {radialLines.map((line, i) => (
        <line
          key={`radial-${i}`}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke="#999"
          strokeWidth="1.5"
          opacity="0.3"
        />
      ))}

      {/* Balanced reference polygon (50% on all axes) */}
      {showBalancedReference && (
        <>
          <path
            d={balancedPath}
            fill="#B0B0B0"
            fillOpacity="0.15"
          />
          <path
            d={balancedPath}
            fill="none"
            stroke="#888"
            strokeDasharray="4,4" // Ensure dashed line
            strokeWidth="2"
            strokeLinejoin="round"
            opacity="0.5"
          />
        </>
      )}

      {/* Data polygon fill */}
      <path
        d={dataPath}
        fill={color}
        fillOpacity="0.2"
      />

      {/* Data polygon stroke */}
      <path
        d={dataPath}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinejoin="round"
      />

      {/* Data points */}
      {dataPoints.map((point, i) => (
        <circle
          key={`point-${i}`}
          cx={point.x}
          cy={point.y}
          r="5"
          fill={color}
          stroke="white"
          strokeWidth="2"
        />
      ))}

      {/* Connecting lines from data points to labels */}
      {/* {showPoleLabels && axisLabels.map((label, i) => {
        const angle = angleForIndex(i);
        const lineStartX = center + (radius + 10) * Math.cos(angle);
        const lineStartY = center + (radius + 10) * Math.sin(angle);
        const lineEndX = center + (radius + 75) * Math.cos(angle);
        const lineEndY = center + (radius + 75) * Math.sin(angle);
        
        return (
          <line
            key={`connector-${i}`}
            x1={lineStartX}
            y1={lineStartY}
            x2={lineEndX}
            y2={lineEndY}
            stroke="#BBBBBB"
            strokeWidth="1.5"
            strokeDasharray="3,3"
            opacity="0.6"
          />
        );
      })} */}

      {/* Connecting lines from data points to labels (end exactly at pill edge) */}
{showPoleLabels && axisLabels.map((label, i) => {
  const angle = angleForIndex(i);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Where the line starts (a bit past the radar radius)
  const lineStartX = center + (radius + 10) * cos;
  const lineStartY = center + (radius + 10) * sin;

  // --- Must mirror the pill sizing used below so math stays in sync ---
  const isMobileSize = size < 700;
  const leaningText = `${label.leaningDirection === 'left' ? '← ' : ''}${label.leaning} · ${label.percentage}%${label.leaningDirection === 'right' ? ' →' : ''}`;
  const maxTextLength = Math.max(label.fullLabel.length, leaningText.length);
  const charWidth = isMobileSize ? 7.5 : 9.5;
  const basePadding = isMobileSize ? 20 : 30;
  const minWidth = isMobileSize ? 160 : 200;
  const pillWidth = Math.max(minWidth, maxTextLength * charWidth + basePadding);
  const pillHeight = isMobileSize ? 50 : 56;

  // Distance from pill center to its edge along the connector angle.
  // We project to the rectangle bounds (half widths) and pick the limiting side.
  const halfW = pillWidth / 2;
  const halfH = pillHeight / 2;
  // Avoid division by zero with very small cos/sin
  const eps = 1e-6;
  const reachX = Math.abs(cos) > eps ? halfW / Math.abs(cos) : Infinity;
  const reachY = Math.abs(sin) > eps ? halfH / Math.abs(sin) : Infinity;
  const reachToEdge = Math.min(reachX, reachY) + 1.5; // + small bleed

  // Pill center (label.x, label.y). Move backwards from center to the edge.
  const lineEndX = label.x - reachToEdge * cos;
  const lineEndY = label.y - reachToEdge * sin;

  return (
    <line
      key={`connector-${i}`}
      x1={lineStartX}
      y1={lineStartY}
      x2={lineEndX}
      y2={lineEndY}
      stroke="#BBBBBB"
      strokeWidth="1.5"
      strokeDasharray="3,3"
      strokeLinecap="round"
      opacity="0.8"
    />
  );
})}

      {/* Simplified axis labels - one per axis */}
      {showPoleLabels && axisLabels.map((label, i) => {
        // Dynamic pill width based on full label length - wider to prevent cutoff
        const textLength = label.fullLabel.length;
        const leaningText = `${label.leaningDirection === 'left' ? '← ' : ''}${label.leaning} · ${label.percentage}%${label.leaningDirection === 'right' ? ' →' : ''}`;
        const maxTextLength = Math.max(label.fullLabel.length, leaningText.length);
        // Responsive sizing based on chart size
        const isMobileSize = size < 700;
        const charWidth = isMobileSize ? 7.5 : 9.5; // Increased character width
        const basePadding = isMobileSize ? 20 : 30;
        const minWidth = isMobileSize ? 160 : 200;
        const pillWidth = Math.max(minWidth, maxTextLength * charWidth + basePadding);
        const pillHeight = isMobileSize ? 50 : 56;
        const mainFontSize = isMobileSize ? '11px' : '13px';
        const subFontSize = isMobileSize ? '10px' : '11px';
        
        return (
          <g key={`axis-label-${i}`}>
            {/* Background pill */}
            <rect
              x={label.x - pillWidth / 2}
              y={label.y - pillHeight / 2}
              width={pillWidth}
              height={pillHeight}
              rx="22"
              fill="white"
              fillOpacity="0.97"
              stroke="#CCCCCC"
              strokeWidth="1.5"
            />
            {/* Axis name */}
            <text
              x={label.x}
              y={label.y - (isMobileSize ? 6 : 7)}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ 
                fontSize: mainFontSize, 
                fontWeight: '600', 
                fontFamily: 'Inter, sans-serif', 
                fill: '#222',
                letterSpacing: '-0.01em'
              }}
            >
              {label.fullLabel}
            </text>
            {/* Leaning indicator */}
            <text
              x={label.x}
              y={label.y + (isMobileSize ? 8 : 10)}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ 
                fontSize: subFontSize, 
                fontWeight: '500', 
                fontFamily: 'Inter, sans-serif', 
                fill: color,
                letterSpacing: '-0.005em'
              }}
            >
              {label.leaningDirection === 'left' && '← '}
              {label.leaning} · {label.percentage}%
              {label.leaningDirection === 'right' && ' →'}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default RadarChart;

